// After a deploy the old hashed chunks are gone, so an open tab's next lazy
// import 404s and the page errors until a full reload (#1279). Vite announces
// a failed dynamic import as `vite:preloadError`; reload once to pick up the
// new index.html. A second failure within RELOAD_WINDOW_MS is left to the
// error boundaries (a real outage, not a stale tab), so this never loops.
// Without sessionStorage there is no way to tell, so it never reloads.
// Offline, a network drop fires the same event; reloading would swap the
// site for the browser's offline page, so it leaves "Try again" to the fan
// (and doesn't use up the reload window).

const KEY = 'scorelm.staleChunkReloadAt'
export const RELOAD_WINDOW_MS = 10_000

function session(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** Reloads unless this tab already reloaded for a stale chunk in the last RELOAD_WINDOW_MS. True when it reloaded. */
export function reloadForStaleChunk(
  now: number = Date.now(),
  storage: Storage | null = session(),
  reload: () => void = () => window.location.reload(),
  online: boolean = typeof navigator === 'undefined' || navigator.onLine !== false,
): boolean {
  if (!online || !storage) return false
  try {
    const last = Number(storage.getItem(KEY) || 0)
    if (now - last >= 0 && now - last < RELOAD_WINDOW_MS) return false
    storage.setItem(KEY, String(now))
  } catch {
    return false
  }
  reload()
  return true
}

/** Listens for Vite's chunk-load failure event. Returns the remover (tests). */
export function installStaleChunkReload(target: Window = window): () => void {
  const onError = () => {
    reloadForStaleChunk()
  }
  target.addEventListener('vite:preloadError', onError)
  return () => target.removeEventListener('vite:preloadError', onError)
}
